import { useCallback, useContext, useMemo, useRef } from "react";
import { canReassignLinkedReconciliationAccount } from "@common/helpers/financialDocument";
import { IntegrationAccount } from "@common/types/integrationAccount";
import {
    ExtractionStatus,
    FinancialDocumentExtended,
} from "@common/types/financialDocument";
import { Entity } from "@common/types/entity";
import { FinancialAccountType } from "@common/types/financialAccount";
import { useEntities, useEntitiesById } from "@hooks/useEntities";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import { useSingleBusinessEntity } from "@hooks/useSingleBusinessEntity";
import { useAutoAssignEntity } from "@hooks/useAutoAssignEntity";
import {
    AccountInfoUpdateBody,
    useAccountInfoUpdateMutation,
    useUpdateFinancialDocumentMetadataMutation,
} from "@api/financialDocument.api";
import { ImporterContext } from "./importer.context";
import { emptyToNull, getLinkedRowTooltipText } from "./helpers";
import { getStatementAccountKey } from "./statementAccountKey";

interface UseImporterAccountEditorParams {
    integrationAccount: IntegrationAccount | undefined;
    financialDocument: FinancialDocumentExtended;
    documentRowId: string;
    shouldAutoAssignEntity?: boolean;
}

export function useImporterAccountEditor({
    integrationAccount,
    financialDocument,
    documentRowId,
    shouldAutoAssignEntity = false,
}: UseImporterAccountEditorParams) {
    const {
        updateDocumentRow,
        revalidateDocument,
        reconciliedFinancialAccount,
        entityIdByStatementAccountKey,
        setStatementAccountEntity,
    } = useContext(ImporterContext);

    const statementAccountKey = getStatementAccountKey(
        financialDocument.accountInfoExtraction,
    );

    const financialAccounts = useFinancialAccounts({
        excludeMockEntities: true,
    });
    const entities = useEntities();
    const entitiesById = useEntitiesById();

    const selectedFinancialAccount = useMemo(() => {
        const accountFromDocument = financialAccounts.find(
            (account) => account.id === financialDocument.financialAccountId,
        );
        if (accountFromDocument) {
            return accountFromDocument;
        }

        if (
            financialDocument.financialAccountId == null &&
            reconciliedFinancialAccount
        ) {
            return reconciliedFinancialAccount;
        }

        return undefined;
    }, [
        financialAccounts,
        financialDocument.financialAccountId,
        reconciliedFinancialAccount,
    ]);

    const financialDocumentRef = useRef(financialDocument);
    financialDocumentRef.current = financialDocument;

    const isAccountLinked = useMemo(
        () => !!integrationAccount?.financialAccountId,
        [integrationAccount],
    );
    const canReassignAccount = canReassignLinkedReconciliationAccount({
        isReconciled: Boolean(financialDocument.isReconciled),
        importerQueue: financialDocument.importerQueue,
    });
    const isAccountAssignmentLocked = isAccountLinked && !canReassignAccount;

    const updateAccountInfo = useAccountInfoUpdateMutation({
        financialDocumentId: financialDocument.id,
        onMutate: (body) => {
            const preMutationDocument = financialDocumentRef.current;
            updateDocumentRow(documentRowId, {
                financialDocument: {
                    ...preMutationDocument,
                    accountInfoExtraction: {
                        ...preMutationDocument.accountInfoExtraction,
                        accountType:
                            body.accountType ??
                            preMutationDocument.accountInfoExtraction
                                ?.accountType ??
                            null,
                        institutionName:
                            body.institutionName !== undefined
                                ? emptyToNull(body.institutionName)
                                : (preMutationDocument.accountInfoExtraction
                                      ?.institutionName ?? null),
                        accountName:
                            body.accountName !== undefined
                                ? emptyToNull(body.accountName)
                                : (preMutationDocument.accountInfoExtraction
                                      ?.accountName ?? null),
                        accountNumber:
                            body.accountNumber !== undefined
                                ? emptyToNull(body.accountNumber)
                                : (preMutationDocument.accountInfoExtraction
                                      ?.accountNumber ?? null),
                        status:
                            preMutationDocument.accountInfoExtraction?.status ??
                            ExtractionStatus.COMPLETED,
                    },
                },
            });
            return preMutationDocument;
        },
        onSuccess: (updatedExtraction) => {
            const currentDocument = financialDocumentRef.current;
            updateDocumentRow(documentRowId, {
                financialDocument: {
                    ...currentDocument,
                    accountInfoExtraction: updatedExtraction,
                },
            });
            revalidateDocument({ financialDocumentId: currentDocument.id });
        },
        onError: (preMutationDocument) => {
            if (preMutationDocument) {
                updateDocumentRow(documentRowId, {
                    financialDocument: preMutationDocument,
                });
                revalidateDocument({
                    financialDocumentId: preMutationDocument.id,
                });
            }
        },
    });

    const updateDocumentMetadata =
        useUpdateFinancialDocumentMetadataMutation(financialDocument);

    const { mutate: mutateMetadata, isPending: isMetadataPending } =
        updateDocumentMetadata;

    const mutateDocumentMetadata = useCallback(
        (
            payload: Parameters<typeof mutateMetadata>[0],
            optimisticDocument: FinancialDocumentExtended,
        ) => {
            mutateMetadata(payload, {
                onSuccess: () => {
                    revalidateDocument({
                        financialDocumentId: optimisticDocument.id,
                    });
                },
                onError: () => {
                    updateDocumentRow(documentRowId, {
                        financialDocument: optimisticDocument,
                    });
                    revalidateDocument({
                        financialDocumentId: optimisticDocument.id,
                    });
                },
            });
        },
        [mutateMetadata, revalidateDocument, updateDocumentRow, documentRowId],
    );

    const { mutate: mutateAccountInfo } = updateAccountInfo;

    const updateAccountInfoField = useCallback(
        (payload: AccountInfoUpdateBody) => {
            if (isAccountLinked) {
                return;
            }
            mutateAccountInfo(payload);
        },
        [mutateAccountInfo, isAccountLinked],
    );

    const onFinancialAccountSelected = useCallback(
        (financialAccountId: number | null) => {
            if (isAccountAssignmentLocked) {
                return;
            }

            const currentDocument = financialDocumentRef.current;
            const selectedAccount =
                financialAccountId != null
                    ? financialAccounts.find((a) => a.id === financialAccountId)
                    : undefined;

            updateDocumentRow(documentRowId, {
                financialDocument: {
                    ...currentDocument,
                    financialAccountId,
                    ...(selectedAccount && {
                        entityId: selectedAccount.entityId ?? null,
                    }),
                },
            });

            mutateDocumentMetadata({ financialAccountId }, currentDocument);
        },
        [
            isAccountAssignmentLocked,
            financialAccounts,
            updateDocumentRow,
            documentRowId,
            mutateDocumentMetadata,
        ],
    );

    const onInstitutionNameChanged = useCallback(
        (institutionName: string) => {
            updateAccountInfoField({
                institutionName,
            });
        },
        [updateAccountInfoField],
    );

    const onAccountTypeChanged = useCallback(
        (accountType: FinancialAccountType) => {
            updateAccountInfoField({
                accountType,
            });
        },
        [updateAccountInfoField],
    );

    const selectedEntityId =
        selectedFinancialAccount?.entityId ?? financialDocument.entityId;

    const persistEntityMetadata = useCallback(
        (entityId: number | null) => {
            if (isAccountLinked) {
                return;
            }

            const currentDocument = financialDocumentRef.current;

            updateDocumentRow(documentRowId, {
                financialDocument: {
                    ...currentDocument,
                    entityId,
                    financialAccountId: null,
                },
            });

            mutateDocumentMetadata(
                { entityId, financialAccountId: null },
                currentDocument,
            );
        },
        [
            isAccountLinked,
            updateDocumentRow,
            documentRowId,
            mutateDocumentMetadata,
        ],
    );

    const onEntityChanged = useCallback(
        (entity: Entity | undefined) => {
            const entityId = entity?.id ?? null;
            if (entityId === selectedEntityId) {
                return;
            }

            persistEntityMetadata(entityId);

            if (!isAccountLinked && statementAccountKey != null) {
                setStatementAccountEntity({
                    key: statementAccountKey,
                    entityId,
                });
            }
        },
        [
            persistEntityMetadata,
            selectedEntityId,
            isAccountLinked,
            statementAccountKey,
            setStatementAccountEntity,
        ],
    );

    const singleBusinessEntity = useSingleBusinessEntity();
    const propagatedEntityId =
        statementAccountKey != null
            ? (entityIdByStatementAccountKey.get(statementAccountKey) ?? null)
            : null;
    const autoAssignEntityId = singleBusinessEntity?.id ?? propagatedEntityId;

    const autoAssignEntity = useCallback(
        (entityId: number) => {
            persistEntityMetadata(entityId);
        },
        [persistEntityMetadata],
    );

    useAutoAssignEntity({
        entityId: autoAssignEntityId,
        currentEntityId: financialDocument.entityId,
        onAssign: autoAssignEntity,
        isBlocked: isAccountLinked || isMetadataPending,
        canAutoAssign:
            shouldAutoAssignEntity &&
            financialDocument.financialAccountId == null,
    });

    const selectedEntity = useMemo(
        () =>
            selectedEntityId != null
                ? entitiesById.get(selectedEntityId)
                : undefined,
        [selectedEntityId, entitiesById],
    );

    const institutionName = selectedFinancialAccount
        ? selectedFinancialAccount.institutionName
        : (financialDocument.accountInfoExtraction?.institutionName ?? null);
    const financialAccountType = selectedFinancialAccount
        ? selectedFinancialAccount.financialType
        : (financialDocument.accountInfoExtraction?.accountType ?? null);
    const isReadOnly = isAccountLinked || Boolean(selectedFinancialAccount);

    const disabledReason = isReadOnly
        ? (getLinkedRowTooltipText(integrationAccount, financialDocument) ??
          "Cannot edit while reconciling.")
        : undefined;

    const accountPickerDisabledReason = isAccountAssignmentLocked
        ? disabledReason
        : undefined;

    return {
        entities,
        selectedFinancialAccount,
        selectedEntity,
        isAccountLinked,
        institutionName,
        financialAccountType,
        isReadOnly,
        disabledReason,
        accountPickerDisabledReason,
        updateAccountInfoField,
        onFinancialAccountSelected,
        onInstitutionNameChanged,
        onAccountTypeChanged,
        onEntityChanged,
    };
}
